import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  Option,
  Box,
  Alert,
  CircularProgress
} from '@mui/joy';
import { Customer, customerService } from '../services/customerService';
import { useDirectoryOrganisationOptions } from '../hooks/useBrands';
import { isValidVatNumber, optionalCustomerText } from '../services/customerMapping';
import { DIRECTORY_PHONE_DUPLICATE_MESSAGE, isDirectoryPhoneDuplicateError } from '../elevate/customers.phone';

interface CustomerEditFormProps {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string, updates: any) => Promise<void>;
}

export const CustomerEditForm: React.FC<CustomerEditFormProps> = ({
  customer,
  open,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    title: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    landline_number: '',
    email: '',
    address: '',
    billing_address: '',
    billing_email_address: '',
    billing_contact_name: '',
    payment_preference: '',
    vat_number: '',
    organization_id: 4,
    organization_name: 'Keystone Glazing'
  });
  // Brands offered for customers (Admin → Organisations), keeping the current one
  const ORGANIZATIONS = useDirectoryOrganisationOptions(formData.organization_name);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customer && customer.source === 'local') {
      // Parse name into first and last name
      const nameParts = customer.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Get organization_id from reference, or try to find it from organization_name
      let orgId = parseInt(customer.reference || '4');
      let orgName = customer.company || 'Keystone Glazing';
      
      // If we have organization_name but no valid reference, try to find the org by name
      if (customer.company && (!customer.reference || customer.reference === '4')) {
        const foundOrg = ORGANIZATIONS.find(org => org.name === customer.company);
        if (foundOrg) {
          orgId = foundOrg.id;
          orgName = foundOrg.name;
        }
      }

      console.log('🔍 CustomerEditForm - Loading customer data:', {
        customerId: customer.id,
        customerCompany: customer.company,
        customerReference: customer.reference,
        resolvedOrgId: orgId,
        resolvedOrgName: orgName,
      });

      setFormData({
        title: customer.title || '',
        first_name: firstName,
        last_name: lastName,
        phone_number: customer.phone,
        landline_number: customer.landline || '',
        email: customer.email || '',
        address: customer.address,
        billing_address: customer.notes || '',
        billing_email_address: customer.billingEmail || '',
        billing_contact_name: customer.billingContact || '',
        payment_preference: customer.paymentPreference || '',
        vat_number: customer.vatNumber || '',
        organization_id: orgId,
        organization_name: orgName
      });
    }
  }, [customer]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.organization_id) {
      newErrors.organization_id = 'Organization is required';
    }

    if (formData.vat_number.trim() && !isValidVatNumber(formData.vat_number)) {
      newErrors.vat_number = 'VAT number must be alphanumeric';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !customer) {
      return;
    }

    setLoading(true);
    try {
      const existing = await customerService.getCustomerByPhone(formData.phone_number.trim());
      if (existing && existing.id !== customer.id) {
        setErrors((prev) => ({ ...prev, phone_number: DIRECTORY_PHONE_DUPLICATE_MESSAGE }));
        setLoading(false);
        return;
      }

      const updates = {
        title: optionalCustomerText(formData.title),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone_number: formData.phone_number.trim(),
        landline_number: optionalCustomerText(formData.landline_number),
        email: optionalCustomerText(formData.email),
        address: formData.address.trim(),
        billing_address: optionalCustomerText(formData.billing_address),
        billing_email_address: optionalCustomerText(formData.billing_email_address),
        billing_contact_name: optionalCustomerText(formData.billing_contact_name),
        payment_preference: optionalCustomerText(formData.payment_preference),
        vat_number: optionalCustomerText(formData.vat_number),
        organization_id: formData.organization_id,
        organization_name: formData.organization_name
      };

      console.log('🔍 CustomerEditForm sending updates:', updates);
      await onSave(customer.id, updates);
      onClose();
    } catch (error) {
      console.error('Error updating customer:', error);
      if (isDirectoryPhoneDuplicateError(error) || (error instanceof Error && error.message === DIRECTORY_PHONE_DUPLICATE_MESSAGE)) {
        setErrors((prev) => ({ ...prev, phone_number: DIRECTORY_PHONE_DUPLICATE_MESSAGE }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general: error instanceof Error ? error.message : 'Failed to update customer',
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (event: React.SyntheticEvent | null, newValue: number | null) => {
    if (newValue) {
      const organization = ORGANIZATIONS.find(org => org.id === newValue);
      setFormData(prev => ({
        ...prev,
        organization_id: newValue,
        organization_name: organization?.name || 'Keystone Glazing'
      }));
    }
  };

  if (!customer || customer.source !== 'local') {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        maxWidth="sm"
        sx={{
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ModalClose />
        <DialogTitle>Edit Customer</DialogTitle>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <DialogContent sx={{ overflow: 'auto', pb: 1 }}>
            <Stack spacing={2}>
              {errors.general && (
                <Alert color="danger" size="sm">{errors.general}</Alert>
              )}
              <FormControl>
                <FormLabel>Title</FormLabel>
                <Select
                  value={formData.title}
                  onChange={(_, value) => setFormData(prev => ({ ...prev, title: value || '' }))}
                  placeholder="Select title"
                >
                  <Option value="">None</Option>
                  <Option value="Mr">Mr</Option>
                  <Option value="Mrs">Mrs</Option>
                  <Option value="Ms">Ms</Option>
                  <Option value="Miss">Miss</Option>
                  <Option value="Dr">Dr</Option>
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl error={!!errors.first_name}>
                  <FormLabel>First Name *</FormLabel>
                  <Input
                    value={formData.first_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                      setFormData(prev => ({ ...prev, first_name: e.target.value }))
                    }
                    placeholder="Enter first name"
                  />
                  {errors.first_name && (
                    <Alert color="danger" size="sm">{errors.first_name}</Alert>
                  )}
                </FormControl>
                <FormControl error={!!errors.last_name}>
                  <FormLabel>Last Name *</FormLabel>
                  <Input
                    value={formData.last_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                      setFormData(prev => ({ ...prev, last_name: e.target.value }))
                    }
                    placeholder="Enter last name"
                  />
                  {errors.last_name && (
                    <Alert color="danger" size="sm">{errors.last_name}</Alert>
                  )}
                </FormControl>
              </Box>

              <FormControl error={!!errors.phone_number}>
                <FormLabel>Phone Number *</FormLabel>
                <Input
                  value={formData.phone_number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData(prev => ({ ...prev, phone_number: e.target.value }))
                  }
                  placeholder="Enter phone number"
                />
                {errors.phone_number && (
                  <Alert color="danger" size="sm">{errors.phone_number}</Alert>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Landline</FormLabel>
                <Input
                  value={formData.landline_number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    if (value.length <= 20) {
                      setFormData(prev => ({ ...prev, landline_number: value }));
                    }
                  }}
                  placeholder="Landline"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData(prev => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="Enter email address"
                />
              </FormControl>

              <FormControl error={!!errors.address}>
                <FormLabel>Address *</FormLabel>
                <Input
                  value={formData.address}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData(prev => ({ ...prev, address: e.target.value }))
                  }
                  placeholder="Enter address"
                />
                {errors.address && (
                  <Alert color="danger" size="sm">{errors.address}</Alert>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Billing Address</FormLabel>
                <Input
                  value={formData.billing_address}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData(prev => ({ ...prev, billing_address: e.target.value }))
                  }
                  placeholder="Enter billing address"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Billing email</FormLabel>
                <Input
                  type="email"
                  value={formData.billing_email_address}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData(prev => ({ ...prev, billing_email_address: e.target.value }))
                  }
                  placeholder="Billing email"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Billing contact</FormLabel>
                <Input
                  value={formData.billing_contact_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData(prev => ({ ...prev, billing_contact_name: e.target.value }))
                  }
                  placeholder="Billing contact"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Payment</FormLabel>
                <Select
                  value={formData.payment_preference}
                  onChange={(_, value) => setFormData(prev => ({ ...prev, payment_preference: value || '' }))}
                  placeholder="Preference"
                >
                  <Option value="">None</Option>
                  <Option value="card">Card</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="cheque">Cheque</Option>
                  <Option value="EFT">EFT</Option>
                </Select>
              </FormControl>

              <FormControl error={!!errors.vat_number}>
                <FormLabel>VAT Number</FormLabel>
                <Input
                  value={formData.vat_number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData(prev => ({ ...prev, vat_number: e.target.value }))
                  }
                  placeholder="VAT number"
                />
                {errors.vat_number && (
                  <Alert color="danger" size="sm">{errors.vat_number}</Alert>
                )}
              </FormControl>

              <FormControl error={!!errors.organization_id}>
                <FormLabel>Organization *</FormLabel>
                <Select
                  value={formData.organization_id}
                  onChange={handleOrganizationChange}
                  placeholder="Select organization"
                >
                  {ORGANIZATIONS.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
                {errors.organization_id && (
                  <Alert color="danger" size="sm">{errors.organization_id}</Alert>
                )}
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="solid"
              disabled={loading}
              startDecorator={loading ? <CircularProgress size="sm" /> : null}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Box>
      </ModalDialog>
    </Modal>
  );
}; 